// The far ring's TEXTURES (APP_HORIZON.md §3-§5, RING TIERS §8c; H1: the far
// field's mesh is the GROUND-M tree's outer nodes, drawn by terrain.js — this
// module keeps no ring mesh). buildRingTex (meta.panorama of the far field's
// data contract + panorama.classes of RING_CLASSES_FORMAT — sky.js
// buildPanorama refuses a bake without them, ringtiers.js ringTilesFault):
// the content-named core (the red world line, a polyline fitted to the
// playable L0 edge, and a 64 m sampler grid, the gazetteer's far-height
// chain) and, per SECTOR of panorama.classes, its texture GENERATED on the
// device at the level ringtiers.js planRing picks for this device and eye
// (ringgen.js, the gensched pass 'ring': the class / light / residual rasters
// painted by the genome + the ring light over the relief of the ground tree's
// outer nodes, on the bake's radiance scale: the material gain
// meta.tex.light.white undoes it, like the terrain) into ONE sector atlas
// (faratlas.js, w38's: terrain.farAtlas — created by the TerrainManager,
// never here). ring.js asks gen.want('ring', tiles) and takes the result in
// setGenerated / fallback.
//
// The shared material rules below (stripMaterial, the edge clamp and the tuck
// light, the optic detail) stay exported: tools/shaderprobe.mjs compiles them
// and the far tree's sector material reuses them.
import * as THREE from 'three';
import { fetchAsset, needGen } from './assetq.js';
import { act, due, slice } from './sched.js';
import { planRingSteps, wantsSteps, genTexel } from './ringtiers.js';
import { ringLook } from './ringlook.js';
import { nodeSide } from '../../groundm/lookup.js';

const RING_MAGIC = 0x524e4731;      // 'RNG1' (the core: 0 chunks, the world line, the sampler)

// the optic sectors' detail: four groundmat layers at two world scales (m)
// (opticDetail takes the groundmat module: this one stays importable by plain
// Node — scripts/check-ring.mjs — groundmat's JSON imports are not)
const DETAIL_M = [2.1, 13.3];
// gensched.js (its JSON imports are not node's either): the generation queue
// the sector textures are asked from (gen.want('ring', ...))
const gensched = () => import('./gensched.js');
// ringgen.js (groundgen's GLSL, JSON imports): the pass 'ring' that paints them
const ringgen = () => import('./ringgen.js');

// the class ids of panorama.classes' cls raster (pipeline/classes.json) that
// pick a detail layer; every other class (and water 254) gets none
export const RING_DETAIL_CLASS = { turf: 1, scree: 4, rock: 5, snow: 15 };

// world position vHazeW comes from the backdrop atmosphere (sky.js makeAtmos);
// rUv = the map's own (clamped) uv (RING_MAP_GLSL). uRingCls: the sector's
// class raster (R8UI, texelFetch: texel (a, b) = cell (a, b), b = 0 the
// NORTH row = v 1 of the map; same rect as the map).
const DETAIL_DECL = `
uniform float uOpticZoom, uMag1x;
uniform vec4 uWedge;
uniform highp sampler2DArray uMatA;
uniform float uMatOn;
uniform highp usampler2D uRingCls;
// a layer's mean-1 albedo multiplier (A rgb x 2); the array wraps itself
vec3 rdtl(vec2 p, int L) {
  return textureGrad(uMatA, vec3(p, float(L)), dFdx(p), dFdy(p)).rgb * 2.0;
}`;
const detailGlsl = (RL) => `
// the optic magnification over the naked eye: uWedge.w = 1 only during an
// optic pass (focus.setPass), the 1x monocular is already uMag1x (3) finer
// than the eye — mag = 1 in every other pass (pixel-unchanged there)
float mag = mix(1.0, max(1.0, uOpticZoom * uMag1x), uWedge.w);
if (mag > 1.0 && uMatOn > 0.5) {
  // full by mag 1.5: no pop as an optic pass begins
  float gz = smoothstep(1.0, 1.5, mag);
  vec3 Wp = vHazeW;
  vec3 nrm = normalize(cross(dFdx(Wp), dFdy(Wp)));
  float fp = length(fwidth(Wp));                       // world metres per pixel
  // zones by CLASS: the class cell under the texel the map sampled (one
  // cell, nearest); no branch on it (the layer reads take derivatives)
  ivec2 kSz = textureSize(uRingCls, 0);
  ivec2 kIx = clamp(ivec2(floor(vec2(rUv.x, 1.0 - rUv.y) * vec2(kSz))), ivec2(0), kSz - 1);
  uint rK = texelFetch(uRingCls, kIx, 0).r;
  float rockW = rK == ${RING_DETAIL_CLASS.rock}u ? 1.0 : 0.0;
  float screeW = rK == ${RING_DETAIL_CLASS.scree}u ? 1.0 : 0.0;
  float turfW = rK == ${RING_DETAIL_CLASS.turf}u ? 1.0 : 0.0;
  float snowW = rK == ${RING_DETAIL_CLASS.snow}u ? 1.0 : 0.0;
  float bare = rockW + screeW + turfW + snowW;          // 0: a class without detail
  vec3 an = abs(nrm);
  vec3 tw = an * an * an * an;
  tw /= (tw.x + tw.y + tw.z);
  vec3 d = vec3(1.0);
  for (int k = 0; k < 2; k++) {
    float S = k == 0 ? ${DETAIL_M[0].toFixed(2)} : ${DETAIL_M[1].toFixed(2)};
    vec3 rk = rdtl(Wp.zy / S, ${RL.rock}) * tw.x + rdtl(Wp.xz / S, ${RL.rock}) * tw.y + rdtl(Wp.xy / S, ${RL.rock}) * tw.z;
    vec3 dk = rk * rockW + rdtl(Wp.xz / S, ${RL.scree}) * screeW + rdtl(Wp.xz / S, ${RL.turf}) * turfW
            + rdtl(Wp.xz / S, ${RL.snow}) * snowW;
    // a tile under ~2 px is noise, not detail: fade by the pixel footprint
    float f = 1.0 - smoothstep(0.25, 0.6, fp / S);
    d *= mix(vec3(1.0), dk, (k == 0 ? 0.55 : 0.45) * f * gz);
  }
  diffuseColor.rgb *= mix(vec3(1.0), d, bare);
}`;

// THE TUCK (ring-tuck-curtain F11, APP_HORIZON.md §8c): inside the frame
// the ring is the tuck — the edge posts at L0 - tuck_m, the inner posts at
// the LOD-envelope minimum - tuck_m (bake.py ring_frame_insert): a skirt
// that dives up to ~170 m below a summit ridge within one 8 m post. Where
// the far terrain draws a coarse level (64 / 128 m between drawn vertices)
// it cuts below a ridge crest at the frame edge and the skirt shows as a
// near-vertical wall (lake-sw: the W edge at z 7858, 122 m above the
// level-4 terrain). Two client rules make it read as the ridge's own flank:
// (1) TEXELS: its uv came from x/z, 1-2 gutter texels stretched up to 21x
//     down the wall, lit by the ring light for the tuck's own lattice (a fake
//     surface 66-102 m under the ground; the edge posts' normals reach one
//     post beyond the frame too). A ring fragment inside the frame or within
//     the margin m beyond it samples the tile at the point m beyond the
//     NEAREST frame side (ringEdgeXZ; GLSL RING_EDGE_GLSL): the ridge's
//     colour at the edge, constant along the side's normal — a wall dipping
//     in from the side carries no texel down its dip. m = one post (an edge
//     post's normal weighs 0 one post out) + RING_EDGE_UV_TEXELS tier-0
//     texels (ringTexel0: the bilinear's reach at mip 0) = 8 + 9.99 ~ 18 m
//     on 8 m posts / the 9.99 m tier-0 generated texel. Clean = every texel CENTRE the filter reads >= one post
//     beyond the frame: mip 0 always; mip 1 only where the 2-texel grid is
//     aligned to the side (live: tile rects on 8 m multiples); mip >= 2 (and
//     a level >= 2 tile, = that mip) reaches back inside the post
//     (APP_HORIZON §5 the tuck). The sample is then held one texel inside
//     the material's OWN rect (ringRectClamp: the shader samples that one
//     texture, never another tile; textureSize = the level loaded)
//     and read with the UNCLAMPED uv's gradients (textureGrad: the side
//     switch at a frame corner is a uv jump, not a minification).
// (2) LIGHT: that colour is lit for the RIDGE (sunlit snow on the crest:
//     linear luminance 0.7-0.99), but the wall faces sideways. A fragment
//     inside the frame takes the world's one light model (bake
//     light_irradiance, sky.js island light: hemisphere by n.y + sun x
//     Lambert) for its OWN facing over the brightest light any face gets
//     (E(n) is linear in n: max = gnd + h + |h y + sunC s|, h = (sky-gnd)/2,
//     ~ a face square to the sun) — whatever the ridge texel was lit for,
//     the wall never shows more light than its own facing: E(n) / E_max
//     (ringTuckShade). (Over E(up) instead, the crossing came out 1.6x
//     brighter than with no rule: the crest faces the sun.) Applied AFTER the
//     optic detail: its zones classify the unshaded tile texel.
export const RING_EDGE_UV_TEXELS = 1;

// the ring's tier-0 texel (m): the finest level-0 generated texel over the
// tiers of panorama.classes (cell_m[tier] / gen.up, ringtiers.js genTexel;
// the frame's sectors are cut from the finest band), else half a post (a
// pano without classes: tools/shaderprobe.mjs's bare fixture)
export function ringTexel0(pano) {
  const c = pano && pano.classes;
  let t = Infinity;
  for (const tier of Object.keys((c && c.cell_m) || {})) t = Math.min(t, genTexel(c, tier));
  return t > 0 && Number.isFinite(t) ? t : 0.5 * pano.post_m;
}

// the playable frame in game metres, the clamp distance m (one post +
// RING_EDGE_UV_TEXELS tier-0 texels) and the light model ({ sun: unit dir in
// game axes, sky, ground, sunC } or null), or null (a pano without
// frame_posts: no rule)
export function ringEdge(pano, meta = null) {
  const fp = pano && pano.frame_posts;
  if (!fp) return null;
  const ps = pano.post_m, ox = pano.origin_game_x, oz = pano.origin_game_z;
  const lt = meta && meta.lighting, lm = meta && meta.tex && meta.tex.light;
  let light = null;
  if (lt && lt.sun_az_deg != null && lt.sun_alt_deg != null && lm && lm.sky && lm.ground && lm.sun) {
    const az = (lt.sun_az_deg * Math.PI) / 180, alt = (lt.sun_alt_deg * Math.PI) / 180;
    // game axes: +x east, +z south, y up (sky.js buildHorizon's sun)
    light = { sun: [Math.sin(az) * Math.cos(alt), Math.sin(alt), -Math.cos(az) * Math.cos(alt)],
      sky: lm.sky, ground: lm.ground, sunC: lm.sun };
  }
  return { rect: [ox + fp[0] * ps, oz + fp[1] * ps, ox + fp[2] * ps, oz + fp[3] * ps],
    m: ps + RING_EDGE_UV_TEXELS * ringTexel0(pano), light };
}

// the texture point a ring fragment at game (x, z) samples: itself, or (inside
// the frame / within edge.m beyond it) the point edge.m beyond the nearest
// frame side. Returns [x, z, clamped, d] (d = distance inside the frame,
// < 0 beyond it). The JS twin of RING_EDGE_GLSL (scripts/check-ring.mjs).
export function ringEdgeXZ(x, z, edge) {
  if (!edge) return [x, z, false, -Infinity];
  const [x0, z0, x1, z1] = edge.rect, m = edge.m;
  const dW = x - x0, dE = x1 - x, dN = z - z0, dS = z1 - z;
  const d = Math.min(dW, dE, dN, dS);
  if (d <= -m) return [x, z, false, d];
  if (d === dW) return [x0 - m, z, true, d];
  if (d === dE) return [x1 + m, z, true, d];
  if (d === dN) return [x, z0 - m, true, d];
  return [x, z1 + m, true, d];
}

// the own-rect hold of a CLAMPED sample: one texel inside the rect of the
// texture it is read from (rect [x0, z0, x1, z1], px [w, h] = that texture's
// size; a 1-texel texture: its middle). The JS twin of the GLSL clamp of rUv
// to [h, 1 - h], h = min(1 / size, 1 / 2).
export function ringRectClamp(x, z, rect, px) {
  const tx = (rect[2] - rect[0]) / Math.max(px[0], 2), tz = (rect[3] - rect[1]) / Math.max(px[1], 2);
  return [Math.min(Math.max(x, rect[0] + tx), rect[2] - tx), Math.min(Math.max(z, rect[1] + tz), rect[3] - tz)];
}

// a tuck fragment's light over the brightest face's, per channel, <= 1: n = its
// unit facet normal (game axes, the upward side: the tuck stands in for the
// ground, and the player inside the frame sees a wall's inward, upward face
// — a level tuck seen from below must not go black). The JS twin of the GLSL.
export function ringTuckShade(n, light) {
  if (!light) return [1, 1, 1];
  const s = light.sun, w = 0.5 * n[1] + 0.5;
  const lam = Math.max(0, n[0] * s[0] + n[1] * s[1] + n[2] * s[2]);
  return [0, 1, 2].map((c) => {
    const ef = light.ground[c] + w * (light.sky[c] - light.ground[c]) + lam * light.sunC[c];
    const h = 0.5 * (light.sky[c] - light.ground[c]), k = light.sunC[c];
    const emax = light.ground[c] + h + Math.hypot(k * s[0], h + k * s[1], k * s[2]);
    return Math.min(1, ef / emax);
  });
}

// uRingEdge = the frame (x0, z0, x1, z1), uRingEdgeM = the clamp distance
// (< 0: no rule), uRingLit = 1 with a light model (uRingSun / uRingSky /
// uRingGnd / uRingSunC). The fragment's game position is the atmosphere's
// vHazeW (sky.js makeAtmos: modelMatrix x transformed after begin_vertex; a
// ring chunk has no morph / skin / instancing / displacement, so transformed
// = position there — what a ring varying of its own would carry)
export const RING_EDGE_GLSL = `
uniform vec4 uRingRect, uRingEdge;
uniform float uRingEdgeM, uRingLit;
uniform vec3 uRingSun, uRingSky, uRingGnd, uRingSunC;
bool ringEdgeXZ(inout vec2 p, out float d) {
  float dW = p.x - uRingEdge.x, dE = uRingEdge.z - p.x, dN = p.y - uRingEdge.y, dS = uRingEdge.w - p.y;
  d = min(min(dW, dE), min(dN, dS));
  if (uRingEdgeM < 0.0 || d <= -uRingEdgeM) return false;
  if (d == dW) p.x = uRingEdge.x - uRingEdgeM;
  else if (d == dE) p.x = uRingEdge.z + uRingEdgeM;
  else if (d == dN) p.y = uRingEdge.y - uRingEdgeM;
  else p.y = uRingEdge.w + uRingEdgeM;
  return true;
}
vec3 ringTuckShade(vec3 n) {
  float lam = max(dot(n, uRingSun), 0.0);
  vec3 ef = mix(uRingGnd, uRingSky, 0.5 * n.y + 0.5) + lam * uRingSunC;
  vec3 h = 0.5 * (uRingSky - uRingGnd);
  vec3 ax = uRingSunC * uRingSun.x, ay = h + uRingSunC * uRingSun.y, az = uRingSunC * uRingSun.z;
  vec3 emax = uRingGnd + h + sqrt(ax * ax + ay * ay + az * az);
  return min(ef / emax, vec3(1.0));
}`;
// three's map chunk on the clamped uv (rUv; every other fragment keeps
// vMapUv), held one texel inside this texture (ringRectClamp), sampled with
// vMapUv's gradients (textureGrad: GLSL ES 3.00 — three r170 compiles every
// non-raw material as #version 300 es, WebGL2 only). The derivatives (facet
// normal, uv gradients) are taken before any branch (undefined in
// non-uniform control flow); a degenerate facet (zero cross) gets n = 0, not
// a NaN. The map chunk must hold its one sample call: else the module throws
// at import (a three upgrade fails check-ring, not silently the rule).
const MAP_SAMPLE = 'texture2D( map, vMapUv )';
if (!THREE.ShaderChunk.map_fragment.includes(MAP_SAMPLE)) {
  throw new Error(`ring.js: three's map_fragment lacks '${MAP_SAMPLE}' (RING_MAP_GLSL)`);
}
export const RING_MAP_GLSL = `#ifdef USE_MAP
vec2 rUv = vMapUv;
float rD;
vec3 rC = cross(dFdx(vHazeW), dFdy(vHazeW));
vec3 rN = rC / max(length(rC), 1e-10);
vec2 rGx = dFdx(vMapUv), rGy = dFdy(vMapUv);
{
  vec2 q = vHazeW.xz;
  if (ringEdgeXZ(q, rD)) {
    vec2 rH = min(1.0 / vec2(textureSize(map, 0)), vec2(0.5));
    rUv = clamp(vec2((q.x - uRingRect.x) * uRingRect.z, 1.0 - (q.y - uRingRect.y) * uRingRect.w), rH, 1.0 - rH);
  }
}
${THREE.ShaderChunk.map_fragment.replace(MAP_SAMPLE, 'textureGrad( map, rUv, rGx, rGy )')}
#endif
`;
// the tuck's own light, after the optic detail (its zones read the unshaded
// tile texel)
export const RING_SHADE_GLSL = `
#ifdef USE_MAP
if (rD > 0.0 && uRingEdgeM >= 0.0 && uRingLit > 0.5) {
  if (rN.y < 0.0) rN = -rN;
  diffuseColor.rgb *= ringTuckShade(rN);
}
#endif`;

// game-space uv for one sector's texture: rect = [x0, z0, x1, z1] incl. the
// gutter (panorama.classes.sectors[id].rect); v = 1 - (z - z0) / h: v 1 is
// the NORTH edge (min game z) — ringgen's generated texture holds that
// (its GL row y, from v 0, centred at z1 - (y + 0.5) texel).
// `detail` (an optic sector): { uniforms } adds the detail modulation.
// `edge` (ringEdge(pano, meta)): the tuck's texel + light rules (above).
// Exported for tools/shaderprobe.mjs (compiles both programs).
export function stripMaterial(map, rect, pano, atmos, white, detail, edge = ringEdge(pano)) {
  const mat = new THREE.MeshBasicMaterial({ map });
  mat.color.setScalar(white);           // linear gain, no colour-space convert
  // drawn after the terrain (the ring slot 90, visframe DRAW_ORDER): strict
  // Less = a depth tie with the frame edge loses
  mat.depthFunc = THREE.LessDepth;
  const L = edge && edge.light;
  const u = {
    uRingRect: { value: new THREE.Vector4(rect[0], rect[1],
      1 / (rect[2] - rect[0]), 1 / (rect[3] - rect[1])) },
    uRingOrig: { value: new THREE.Vector3(pano.origin_game_x, pano.origin_game_z, pano.post_m) },
    uRingEdge: { value: edge ? new THREE.Vector4(...edge.rect) : new THREE.Vector4() },
    uRingEdgeM: { value: edge ? edge.m : -1 },
    uRingLit: { value: L ? 1 : 0 },
    uRingSun: { value: new THREE.Vector3(...(L ? L.sun : [0, 1, 0])) },
    uRingSky: { value: new THREE.Vector3(...(L ? L.sky : [1, 1, 1])) },
    uRingGnd: { value: new THREE.Vector3(...(L ? L.ground : [1, 1, 1])) },
    uRingSunC: { value: new THREE.Vector3(...(L ? L.sunC : [0, 0, 0])) },
    ...(detail ? detail.uniforms : {}),
  };
  atmos(mat, null, (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform vec4 uRingRect;\nuniform vec3 uRingOrig;')
      .replace('#include <uv_vertex>', `#include <uv_vertex>
  vMapUv = vec2((uRingOrig.x + position.x * uRingOrig.z - uRingRect.x) * uRingRect.z,
                1.0 - (uRingOrig.y + position.z * uRingOrig.z - uRingRect.y) * uRingRect.w);`);
    // map (clamped uv) -> the optic detail (unshaded texel) -> the tuck light
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>' + RING_EDGE_GLSL + (detail ? DETAIL_DECL : ''))
      .replace('#include <map_fragment>', RING_MAP_GLSL + (detail ? detail.glsl : '') + RING_SHADE_GLSL);
  }, detail ? 'ring-optic' : 'ring');
  return mat;
}

// bilinear sampler over a row-major u16 grid (null outside it; hold: a point outside held on its edge)
export function gridSampler(data, N, sp, ox, oz, zScale, zBase, hold = false) {
  const h = (i, j) => data[j * N + i] * zScale + zBase, lim = N - 1 - 1e-9;
  return (x, z) => {
    let fi = (x - ox) / sp, fj = (z - oz) / sp;
    if (hold) { fi = clampN(fi, 0, lim); fj = clampN(fj, 0, lim); }
    const i0 = Math.floor(fi), j0 = Math.floor(fj);
    if (i0 < 0 || j0 < 0 || i0 >= N - 1 || j0 >= N - 1) return null;
    const di = fi - i0, dj = fj - j0;
    return h(i0, j0) * (1 - di) * (1 - dj) + h(i0 + 1, j0) * di * (1 - dj)
      + h(i0, j0 + 1) * (1 - di) * dj + h(i0 + 1, j0 + 1) * di * dj;
  };
}

const clampN = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// The red world line: ONE ribbon mesh along the frame edge, -0.5..+2.5 m
// around the playable L0 surface (shared by both sides). bake_panorama's
// polyline (_ring_border) is fitted to the 0.5 m edge cells within
// border.tol_m: s = arc length from the NW corner, N (W->E), E (N->S),
// S (E->W), W (S->N), in border.unit_m; game x/z = 0 at the frame's NW corner.
// borderLine: the polyline's points { x, y, z } (game metres) — what the
// ribbon is drawn around; scripts/check-ring.mjs measures it against L0.
export function borderLine(ab, pano) {
  const bd = pano.border;
  const n = bd.n;
  const zScale = pano.z_scale_cm / 100, zBase = pano.z_base_m;
  const sArr = new Uint32Array(ab, bd.off, n);
  const yArr = new Uint16Array(ab, bd.off + 4 * n, n);
  const [Wm, Hm] = bd.frame_m;
  const x = new Float64Array(n), y = new Float64Array(n), z = new Float64Array(n);
  for (let a = 0; a < n; a++) {
    const d = sArr[a] * bd.unit_m;
    y[a] = yArr[a] * zScale + zBase;
    if (d <= Wm) { x[a] = d; z[a] = 0; }                                // N
    else if (d <= Wm + Hm) { x[a] = Wm; z[a] = d - Wm; }                // E
    else if (d <= 2 * Wm + Hm) { x[a] = 2 * Wm + Hm - d; z[a] = Hm; }   // S
    else { x[a] = 0; z[a] = 2 * Wm + 2 * Hm - d; }                      // W
  }
  return { x, y, z };
}

function borderMesh(ab, pano) {
  const L = borderLine(ab, pano);
  const n = pano.border.n;
  const pos = new Float32Array(n * 6);
  let k = 0;
  for (let a = 0; a < n; a++) {
    pos[k++] = L.x[a]; pos[k++] = L.y[a] - 0.5; pos[k++] = L.z[a];
    pos[k++] = L.x[a]; pos[k++] = L.y[a] + 2.5; pos[k++] = L.z[a];
  }
  const idx = new Uint32Array(n * 6);
  for (let a = 0, q = 0; a < n; a++) {
    const b = (a + 1) % n;
    idx[q++] = 2 * a; idx[q++] = 2 * b; idx[q++] = 2 * a + 1;
    idx[q++] = 2 * a + 1; idx[q++] = 2 * b; idx[q++] = 2 * b + 1;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  geo.computeBoundingSphere();
  const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
    color: 0xd01818, side: THREE.DoubleSide, fog: true,
  }));
  mesh.name = 'world-border';
  return mesh;
}

// The optic sectors' detail (stripMaterial `detail`): uniforms + GLSL, from
// the groundmat module `gm` (its shared uniforms, its layer indices). The
// zones are the sector's class raster (uRingCls): a material using it gives
// each optic material its own uRingCls (the sector's cls texture); the one
// here is the declaration's placeholder (tools/shaderprobe.mjs
// compiles it). meta / pano: the call shape shaderprobe passes.
export function opticDetail(meta, pano, opts, gm) {
  const { GU, GM_LAYER } = gm;
  return { uniforms: {
    // focus.uZoom / focus.uWedge: the SAME objects (focus writes them in
    // place every optic pass, never reassigns them)
    uOpticZoom: opts.zoom || { value: 1 },
    uWedge: opts.wedge || { value: new THREE.Vector4(0, 0, 0, 0) },
    uMag1x: { value: opts.mag1x > 0 ? opts.mag1x : 3 },
    // groundmat's shared uniform objects: the arrays swap in when they land
    uMatA: GU.uMatA,
    uMatOn: GU.uMatOn,
    uRingCls: { value: null },
  }, glsl: detailGlsl(GM_LAYER) };
}

// ------------------------------------------------------------ the relief
// THE RELIEF a sector is painted over: the ground tree's outer nodes
// (groundm/index.json rows k >= meta.groundm.outer.first, an apron row
// {apron: true} left out: the S seam's tuck, not ground) covering the
// sector's rect at the FINEST level present there — a node is taken where at
// least one of its children meeting the rect is missing from the index (its
// present children are taken too: the raster draws coarse -> fine, the finest
// wins, ringref rasterHeights). rows: Map `${k}_${tx}_${ty}` -> row; rect
// [x0, z0, x1, z1] game m (a node's box: tx x nodeSide(k) .. + side, ty ...)
// -> [row] (no order promised)
export const nodeKey = (k, tx, ty) => `${k}_${tx}_${ty}`;
export function reliefNodes(rows, outer, rect) {
  const first = outer.first, top = outer.top, out = [];
  const hits = (k, tx, ty) => {
    const s = nodeSide(k);
    return tx * s < rect[2] && (tx + 1) * s > rect[0] && ty * s < rect[3] && (ty + 1) * s > rect[1];
  };
  const pick = (r) => {
    if (r.k <= first) { out.push(r); return; }
    const kids = [];
    let all = true;
    for (let d = 0; d < 4; d++) {
      const cx = 2 * r.tx + (d & 1), cy = 2 * r.ty + (d >> 1);
      if (!hits(r.k - 1, cx, cy)) continue;
      const c = rows.get(nodeKey(r.k - 1, cx, cy));
      if (c) kids.push(c); else all = false;
    }
    if (!all) out.push(r);
    for (const c of kids) pick(c);
  };
  for (const r of rows.values()) {
    // the roots: a row meeting the rect whose parent is not a row (or the top)
    if (r.k >= top || !rows.has(nodeKey(r.k + 1, r.tx >> 1, r.ty >> 1))) if (hits(r.k, r.tx, r.ty)) pick(r);
  }
  return out;
}

// -> {ab: the RNG1 core, or null (404 / failed / not a ring file)}
async function fetchCore(url) {
  const r = await fetchAsset(url, { prio: 'bulk', as: 'arrayBuffer', status: true });
  const ab = r.body;
  return ab && ab.byteLength >= 8 && new DataView(ab).getUint32(0, false) === RING_MAGIC ? ab : null;
}

// The far ring's textures: returns rt = { look, sectors, border, height,
// atlas, version, onChange(fn), want(view), ready(id), relief(id),
// setGenerated(id, level), fallback(id, rgb), commit(entry), quick(entry, rgb),
// held }. THROWS on a bake without meta.tex.light.white (checked before any
// fetch) and on a core the queue could not fetch. opts: { atlas
// (terrain.farAtlas, or null: no texture is generated — border and height
// still are), getRenderer, gmIndex (the ground tree's index promise,
// groundtree.js indexRaw: its 'near' fetch shared — the relief rows never
// wait out the beam pause; none: fetched here, bulk) }.
//   look      ringLook(panorama.classes) (the far tree's sector chooser)
//   sectors   Map id -> { i (look.sectors index), id, tier, rect (incl. the
//             gutter), px (the class raster), level (the resident generated
//             level, -1 none), quick (the QUICK paint's level resident, -1
//             none), fallback (the flat sRGB8 rgb, or null) }
//   border    the red world line mesh (borderLine) or null; height (x, z) ->
//             the far height (gridSampler over the core's sampler) or null;
//             held (x, z) the same held on the sampler's edge (the quick
//             paint: a sector's gutter reaches past the core)
//   want(view) view = { eye: {x, z} | null, pa, k }: the plan (planRing over
//             classes.sectors, coarsened until atlas.budget.fits holds) ->
//             gen.want('ring', tiles, { eye }), one tile per sector { id,
//             level, tier, rect, px: [w >> level, h >> level], cls, lit, res
//             (urls) } at the plan level, or at its resident level while that
//             is at least as fine and the kept set fits (ringtiers wants);
//             and the relief nodes of every wanted sector fetched (bulk). Boot
//             (buildRingTex): want({ eye: null }) = the coarsest everywhere.
//   ready(id) every relief node of the sector's rect fetched (or given up:
//             a 404); relief(id) -> [{ k, tx, ty, bytes }] the fetched ones
//             (ringgen decodes them: groundm/codec.js)
//   commit(entry) the pass's painted atlas entry (atlas.alloc(id, px,
//             level)): a coarser level than the resident is dropped
//             (atlas.free(id, true): the pending alloc only), else
//             atlas.commit(entry) -> setGenerated(id, level)
//   quick(entry, rgb) the QUICK paint's entry (ringgen.js: the sector's
//             first colour, before its relief is in): committed as a
//             provisional atlas entry (atlas.commit(entry, rgb): evicted to
//             flat rgb when a painted entry needs the room) unless a painted
//             level landed first; the sector's level stays -1 (the plan
//             still paints it)
//   fallback(id, rgb) the pass gave the sector up (a deterministic failure:
//             gensched FAILURES): without a generated or quick level it draws flat in
//             rgb (sRGB8: ringref ringFallbackRGB) — atlas.fallback(id, rgb)
//   version / onChange(fn): bumped / fn(ids) on every landing, fallback and
//             atlas loss (a lost context: the atlas drops every entry ->
//             those sectors' levels reset and the last plan is wanted again)
// DROPS (need-drop-rerequest, APP_PLAYER §7): a relief node the asset
// queue's need score dropped is asked again by the next want() once the view
// moved on from the one it dropped at (assetq needGen). A 404 / failed node
// is given up.
export async function buildRingTex(meta, baseUrl, atmos, opts = {}) {
  if (!(meta.tex && meta.tex.light && meta.tex.light.white > 0)) {
    throw new Error('bake has no meta.tex.light.white (texture rev >= 3 required)');
  }
  const pano = meta.panorama, C = pano.classes, K = pano.core;
  const atlas = opts.atlas || null;
  const core = await fetchCore(`${baseUrl}/${K.file}`);
  if (!core) throw new Error(`[ring] ${K.file} not loaded`);
  const zScale = pano.z_scale_cm / 100, zBase = pano.z_base_m;
  const border = pano.border ? borderMesh(core, { ...pano, border: { ...pano.border, off: K.border.off } }) : null;
  const N = pano.sampler.n;
  const grid = [new Uint16Array(core, K.sampler.off, N * N), N, pano.sampler.step_posts * pano.post_m, pano.origin_game_x, pano.origin_game_z, zScale, zBase];
  const height = gridSampler(...grid), held = gridSampler(...grid, true);
  const look = ringLook(C);
  const url = (f) => `${baseUrl}/${C.dir}/${f}`;
  // per sector: the resident level, the gen tile per level (one object: gen.want keeps its asks),
  // its relief node keys (null until the index is in)
  const sectors = new Map(), tileCache = new Map(), nodesOf = new Map();
  look.sectors.forEach((s, i) => {
    const q = C.sectors[s.id];
    sectors.set(s.id, { i, id: s.id, tier: q.tier, rect: q.rect, px: q.px, level: -1, quick: -1, fallback: null });
    tileCache.set(s.id, []);
  });
  const tileOf = (st, level) => {
    const T = tileCache.get(st.id), q = C.sectors[st.id];
    return T[level] || (T[level] = { id: st.id, level, tier: st.tier, rect: st.rect,
      px: [st.px[0] >> level, st.px[1] >> level], cls: url(q.cls.file), lit: url(q.lit.file), res: url(q.res.file) });
  };
  // THE RELIEF NODES: the outer rows of the ground tree's index (no far tree: none -> every
  // sector given up flat by the pass); node key -> { k, tx, ty, st: idle | busy | ok | fail, bytes, drop }
  const outer = meta.groundm && meta.groundm.outer;
  const rows = new Map(), nodes = new Map();
  if (atlas && outer) {
    try {
      const ixFile = meta.groundm.index || 'groundm/index.json';
      const ix = await (opts.gmIndex || fetchAsset(`${baseUrl}/${ixFile}`, { prio: 'bulk', as: 'json' }));
      for (const n of (ix && ix.nodes) || []) if (n.k >= outer.first && n.k <= outer.top && !n.apron) rows.set(nodeKey(n.k, n.tx, n.ty), n);
    } catch (e) { console.warn('[ring] groundm index not loaded: no relief', e && e.message); }
  }
  const keysOf = (id) => {
    let ks = nodesOf.get(id);
    if (!ks) nodesOf.set(id, (ks = reliefNodes(rows, outer || { first: 0, top: 0 }, sectors.get(id).rect).map((r) => nodeKey(r.k, r.tx, r.ty))));
    return ks;
  };
  const loadNode = async (key) => {
    let n = nodes.get(key);
    if (!n) { const r = rows.get(key); nodes.set(key, (n = { k: r.k, tx: r.tx, ty: r.ty, st: 'idle', bytes: null, drop: -1 })); }
    // one request per node; a drop is asked again once the view moved on
    if (n.st !== 'idle' || (n.drop >= 0 && n.drop === needGen())) return;
    n.st = 'busy';
    try {
      const r = await fetchAsset(`${baseUrl}/groundm/L${n.k}/${n.tx}_${n.ty}.bin`, { prio: 'bulk', status: true });
      if (r.body) { n.bytes = r.body; n.st = 'ok'; act('ring.relief'); } else if (r.dropped) { n.drop = r.gen; n.st = 'idle'; } else n.st = 'fail';
    } catch (e) {
      n.st = 'fail';
      console.warn('[ring] relief node not loaded:', key, e && e.message);
    }
  };
  const subs = [];
  let lastView = { eye: null }, gen = null;   // the generation queue (gensched gen; null without an atlas)
  const loaded = () => new Map([...sectors].map(([id, st]) => [id, { tex: st.level }]));
  // THE RE-PLAN ON THE FRAME BUDGET (xr-stalls): planRing's dry-run packs (faratlas
  // fitsSteps, a step per box) run as steps under sched.js due() / slice() — the task
  // that asks (main.js ringTick, in the frame) runs steps only while its slice lasts, the
  // rest goes on in sliced tasks (in a headset xr_share x the XR frame). Latest wins: a
  // newer want supersedes a plan still running (its result is never applied). -> a
  // promise, settled once this want's plan is applied (or superseded)
  let planTok = 0;
  function* applySteps(view) {
    const fits = atlas ? atlas.budget.fitsSteps : null;
    const plan = yield* planRingSteps(C, { eye: view.eye || null, pa: view.pa, k: view.k, fits });
    return { plan, want: yield* wantsSteps(plan, loaded(), fits) };
  }
  const apply = async (view) => {
    lastView = view;
    const tok = ++planTok, it = applySteps(view);
    let r;
    for (;;) {
      r = act('ring.plan', () => { let s; do s = it.next(); while (!s.done && !due()); return s; });
      if (r.done) break;
      await slice();
      if (tok !== planTok) return;
    }
    const tiles = [], { want } = r.value;
    // nearest first is the pass's need; the asset queue's bulk lane bounds the fetches
    for (const [id, w] of want) {
      const st = sectors.get(id);
      tiles.push(tileOf(st, w.tex >= 0 ? w.tex : st.level));
      for (const key of keysOf(id)) loadNode(key);
    }
    if (gen) gen.want('ring', tiles, { eye: view.eye || null });
  };
  const bump = (ids) => { rt.version++; for (const fn of subs) fn(ids); };
  const rt = { look, sectors, border, height, held, atlas, version: 0,
    onChange(fn) { subs.push(fn); },
    want(view) { return apply(view || { eye: null }); },
    // every relief node of the sector's rect fetched or given up
    ready(id) {
      for (const key of keysOf(id)) { const n = nodes.get(key); if (!n || n.st === 'busy' || n.st === 'idle') return false; }
      return true;
    },
    relief(id) {
      const out = [];
      for (const key of keysOf(id)) { const n = nodes.get(key); if (n && n.st === 'ok') out.push({ k: n.k, tx: n.tx, ty: n.ty, bytes: n.bytes }); }
      return out;
    },
    // the pass's painted entry: a coarser level than the resident one is dropped (its pending alloc
    // freed), else committed (pad + mips + the atlas's swap) -> true
    commit(e) {
      const st = sectors.get(e.id);
      if (st.level >= 0 && st.level < e.level) { atlas.free(e.id, true); return false; }
      atlas.commit(e);
      rt.setGenerated(e.id, e.level);
      return true;
    },
    // the quick paint's entry: provisional (atlas.commit(e, rgb)), unless a painted level is in
    quick(e, rgb) {
      const st = sectors.get(e.id);
      if (st.level >= 0) { atlas.free(e.id, true); return false; }
      atlas.commit(e, rgb);
      act('ring.quick');
      st.quick = e.level;
      st.fallback = null;
      bump([e.id]);
      return true;
    },
    setGenerated(id, level) {
      const st = sectors.get(id);
      act('ring.tile');
      st.level = level;
      st.quick = -1;
      st.fallback = null;
      bump([id]);
    },
    // DETERMINISTICALLY given up (ringgen pass.fail: a bad class byte, a wrong size, a sha mismatch,
    // no relief — gensched FAILURES): with no generated or quick level it draws FLAT in rgb (sRGB8: ringref
    // ringFallbackRGB, the world's mean ring colour under its flat light) — never a missing mountain
    fallback(id, rgb) {
      const st = sectors.get(id);
      if (st.level >= 0 || st.quick >= 0 || st.fallback) return;
      st.fallback = [rgb[0], rgb[1], rgb[2]];
      if (atlas) atlas.fallback(id, st.fallback);
      bump([id]);
    } };
  // a lost atlas entry (a lost context: onChange(every id) with nothing resident): that sector's level
  // resets and the last plan is wanted again — after an empty want, so gensched forgets its done
  // `id@level` keys (the ring pass holds no slot: an unwanted entry is dropped) and paints them anew
  if (atlas) {
    atlas.onChange((ids) => {
      let lost = false;
      for (const id of ids) {
        const st = sectors.get(id);
        if (!st || (st.level < 0 && st.quick < 0 && !st.fallback) || atlas.lookup(id)) continue;
        st.level = -1; st.quick = -1; st.fallback = null; lost = true;
      }
      if (lost) {
        bump(ids);
        queueMicrotask(() => { if (gen) gen.want('ring', [], { eye: lastView.eye || null }); apply(lastView); });
      }
    });
  }
  // no atlas (no far tree): nothing is generated
  if (atlas) {
    gen = (await gensched()).gen;
    (await ringgen()).installRingGen(gen, rt, meta, baseUrl, opts.getRenderer);
  }
  apply({ eye: null });
  return rt;
}
